import React from 'react';
import { useLanguage } from '@/App';
import { Instagram, Linkedin, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

// Footer — kompakt tek satir yapi (mobile'da 3 grup alt alta).
// Kategori basliklari ("Iletisim:", "Hizli Erisim", "Bizi Takip Edin") kaldirildi.
// Sosyal medya ikonlari hover tooltip ile amaclarini aciklar (IG/LI: "Bizi Takip Edin", WA: "Bize WhatsApp ile Ulasin").
// Dikey padding py-12 -> py-6 ile yukseklik yari yariya inmis durumda.
const Footer = () => {
  const { language } = useLanguage();

  const content = {
    tr: {
      followUs: 'Bizi Takip Edin',
      whatsappContact: 'Bize WhatsApp ile Ulaşın',
      home: 'Ana Sayfa',
      about: 'Hakkımızda',
      brandApplication: 'Marka Talep Formu',
      influencerApplication: 'Influencer Kayıt',
      privacyPolicy: 'Gizlilik Politikası',
      allRightsReserved: 'Tüm hakları saklıdır.',
    },
    en: {
      followUs: 'Follow Us',
      whatsappContact: 'Reach Us via WhatsApp',
      home: 'Home',
      about: 'About Us',
      brandApplication: 'Brand Application',
      influencerApplication: 'Influencer Application',
      privacyPolicy: 'Privacy Policy',
      allRightsReserved: 'All rights reserved.',
    },
  };

  const t = content[language];

  // App.js'deki gercek route slug'larina uygun: /en/about, /en/influencer-registration
  const routes = language === 'tr'
    ? {
        home: '/',
        about: '/hakkimizda',
        brandForm: '/hakkimizda#brand-form',
        influencer: '/influencer-kayit',
        privacy: '/gizlilik-politikasi',
      }
    : {
        home: '/en',
        about: '/en/about',
        brandForm: '/en/about#brand-form',
        influencer: '/en/influencer-registration',
        privacy: '/en/privacy-policy',
      };

  // Sosyal medya / WhatsApp URL'leri korundu
  const INSTAGRAM_URL = 'https://www.instagram.com/personaajans';
  const LINKEDIN_URL = 'https://www.linkedin.com/company/personaajans/';
  const WHATSAPP_URL = 'https://wa.me/905366141896';

  // Tooltip stili — Tailwind group + group-hover ile fade. Alt taraftan ucgen ok.
  const tooltipBase =
    'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 shadow-lg border border-gray-800';
  const tooltipArrow =
    'absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900';
  const iconBtnClass =
    'w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FFDD59]/20 hover:text-[#FFDD59] flex items-center justify-center text-gray-400 transition-all';

  return (
    <footer
      className="border-t border-gray-800 bg-black mt-auto"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Ana icerik — md+ tek satir 3 grup, mobile alt alta */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">

          {/* SOL: Logo + Email yan yana ("Iletisim:" basligi kaldirildi) */}
          <div className="flex items-center gap-4 justify-center md:justify-start">
            <img
              src="https://customer-assets.emergentagent.com/job_influencer-hub-70/artifacts/tkwsip7b_kisaerkeklogo.png"
              alt="Persona Ajans"
              className="h-8"
            />
            <a
              href="mailto:info@personaajans.com"
              className="text-gray-400 hover:text-[#FFDD59] text-sm transition-colors"
            >
              info@personaajans.com
            </a>
          </div>

          {/* ORTA: 5 link yatay tek satirda (mobile'da gerekirse wrap) */}
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to={routes.home}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t.home}
            </Link>
            <Link
              to={routes.about}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t.about}
            </Link>
            <Link
              to={routes.brandForm}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t.brandApplication}
            </Link>
            <Link
              to={routes.influencer}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t.influencerApplication}
            </Link>
            <Link
              to={routes.privacy}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {t.privacyPolicy}
            </Link>
          </nav>

          {/* SAG: 3 ikon (IG, LI, WA) yan yana + hover tooltip */}
          <div className="flex items-center gap-3 justify-center md:justify-end">
            {/* Instagram — tooltip "Bizi Takip Edin" */}
            <div className="relative group">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={iconBtnClass}
                aria-label="Instagram"
                data-testid="instagram-link"
              >
                <Instagram size={18} />
              </a>
              <span className={tooltipBase}>
                {t.followUs}
                <span className={tooltipArrow}></span>
              </span>
            </div>

            {/* LinkedIn — tooltip "Bizi Takip Edin" */}
            <div className="relative group">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={iconBtnClass}
                aria-label="LinkedIn"
                data-testid="linkedin-link"
              >
                <Linkedin size={18} />
              </a>
              <span className={tooltipBase}>
                {t.followUs}
                <span className={tooltipArrow}></span>
              </span>
            </div>

            {/* WhatsApp — tooltip "Bize WhatsApp ile Ulasin" (eski CTA link kaldirildi) */}
            <div className="relative group">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={iconBtnClass}
                aria-label="WhatsApp"
                data-testid="whatsapp-link"
              >
                <MessageCircle size={18} />
              </a>
              <span className={tooltipBase}>
                {t.whatsappContact}
                <span className={tooltipArrow}></span>
              </span>
            </div>
          </div>
        </div>

        {/* Alt bar — copyright (yil dinamik) */}
        <div className="border-t border-gray-800 pt-4">
          <p className="text-gray-400 text-xs text-center">
            © {new Date().getFullYear()} Persona Ajans. {t.allRightsReserved}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
